import { z } from "zod";
import { AI_TURN_TYPES } from "./turn.js";

/**
 * 🔴 US-AIV2-0024e-S03 (@UC0234 @UC0235) — ĐỌC LẠI lịch sử hội thoại v2.
 *
 * QĐ người dùng 23/09: *"load lại lịch sử thì format giống như khi tôi chat. Chỉ là không edit được"*.
 * ⇒ Một dòng `ai` trả về **nguyên `AiTurn`** (`turn`), không phải một chuỗi rút gọn: đọc lại phải ra
 * đúng thẻ xác nhận / nút / ô đang hỏi mà người dùng đã thấy.
 *
 * 🔴 `disabled: true` trên MỌI chip của lượt cũ — vô hiệu ở **tầng dữ liệu**, không trông chờ client
 * tự nhớ. Bấm được rồi mới báo lỗi là mời người dùng vào cửa khoá (#852 HIGH-1).
 */
export const AI_HISTORY_MAX_LIMIT = 50;
export const AI_HISTORY_DEFAULT_LIMIT = 30;
export const AI_HISTORY_MAX_CURSOR_LENGTH = 256;

/**
 * 🔴 US-AIV2-0120 S05 (@UC0380) — trần TÊN hội thoại. MỘT con số cho hai chỗ: server tự sinh tên (câu đầu cắt ở đây —
 * `ChatHistoryService.record`) và người dùng đổi tên. Lệch nhau thì tên tự sinh dài hơn trần đổi tên ⇒ người dùng không lưu lại
 * được chính tên cũ.
 */
export const AI_CONVERSATION_TITLE_MAX = 60;

/**
 * US-AIV2-0120 (review 0120 security L1/L2) — ký tự CẤM trong tên hội thoại và chữ tìm: điều khiển (`\p{Cc}` — xuống dòng, tab, NUL ⇒
 * Postgres từ chối byte 0 = 500), ngắt dòng/đoạn Unicode (U+2028/2029), điều hướng hai chiều (U+202A–202E, U+2066–2069 — đảo chiều hiển
 * thị), ký tự vô hình (U+200B, U+FEFF — tên "trống" lọt qua `min(1)`) và nửa cặp UTF-16 lẻ (`\p{Cs}`, vd `"\ud800"` trong JSON). GIỮ
 * U+200D (ZWJ) vì emoji ghép cần nó.
 */
const AI_CONVERSATION_TEXT_SAFE = /^[^\p{Cc}\p{Cs}\u2028\u2029\u202A-\u202E\u2066-\u2069\u200B\uFEFF]*$/u;

/** Chip của lượt CŨ: y hệt chip thường, cộng cờ vô hiệu — client không phải suy. */
export const aiHistoryChipSchema = z
  .object({
    kind: z.string().min(1).max(32),
    label: z.string().min(1).max(64),
    payload: z.record(z.string(), z.string()).readonly().default({}),
    primary: z.literal(true).optional(),
    /** 🔴 LUÔN `true` ở lịch sử. `z.literal` ⇒ "chip lịch sử bấm được" là trạng thái KHÔNG BIỂU DIỄN ĐƯỢC. */
    disabled: z.literal(true),
  })
  .strict();

/** Một lượt trong lịch sử. `role:"me"` ⇒ chỉ `content`; `role:"ai"` ⇒ có `turn` để vẽ lại đúng hình dạng. */
export const aiHistoryMessageSchema = z
  .object({
    id: z.string().uuid(),
    role: z.enum(["me", "ai"]),
    /** NGUYÊN VĂN (@UC0234) — server không trim; câu gõ đã chuẩn hoá NFC ở biên lượt (AI-INTERP N5 · `aiTurnRequestSchema.text`), ngoài ra không đổi chữ nào. */
    content: z.string(),
    createdAt: z.string().datetime(),
    /**
     * Hình dạng lượt, chip đã vô hiệu. Vắng ở dòng `me`.
     *
     * 🔴 Khai TƯỜNG MINH thay vì `aiTurnSchema.omit({chips})`: `aiTurnSchema` có `.superRefine`
     * (ZodEffects) nên không `.omit()` được — và quan trọng hơn, ràng buộc của nó viết cho lượt
     * ĐANG SỐNG (vd. `confirm` phải có chip confirm). Lượt CŨ có chip vô hiệu nên vi phạm đúng
     * ràng buộc đó. Dùng lại schema sống ở đây sẽ chặn chính dữ liệu hợp lệ của lịch sử.
     * `.passthrough()` KHÔNG dùng: giữ `.strict()` để server đời sau thêm field thì TS đỏ trước
     * khi mobile im lặng bỏ qua (luật của `turn.ts`).
     */
    turn: z
      .object({
        type: z.enum(AI_TURN_TYPES),
        text: z.string(),
        chips: z.array(aiHistoryChipSchema).readonly(),
        taskId: z.string().optional(),
        taskState: z.string().optional(),
        page: z.string().optional(),
        slot: z.object({ id: z.string(), type: z.string() }).strict().optional(),
        card: z.object({ title: z.string().optional(), lines: z.array(z.object({ label: z.string(), value: z.string() }).strict()).readonly() }).strict().optional(),
        warning: z.string().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export const aiHistoryConversationSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string(),
    groupId: z.string().uuid().nullable(),
    lastMessageAt: z.string().datetime(),
  })
  .strict();

export const aiHistoryListQuerySchema = z
  .object({
    cursor: z.string().max(AI_HISTORY_MAX_CURSOR_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(AI_HISTORY_MAX_LIMIT).optional(),
  })
  .strict();

/**
 * 🔴 US-AIV2-0120 S06 (@UC0382) — query của DANH SÁCH hội thoại: cursor/limit như cũ + `q` (tìm theo tên, server ILIKE trong phạm vi
 * chủ — danh sách có cursor nên lọc ở client chỉ thấy trang đã tải). Route đọc TIN NHẮN giữ `aiHistoryListQuerySchema` (không `q`).
 * `q` rỗng sau khi cắt ⇒ coi như vắng.
 */
export const aiHistoryConversationsQuerySchema = aiHistoryListQuerySchema
  .extend({ q: z.string().trim().max(AI_CONVERSATION_TITLE_MAX).regex(AI_CONVERSATION_TEXT_SAFE).optional() })
  .strict();

/**
 * 🔴 US-AIV2-0120 S05 (@UC0380) — `PATCH /v1/ai/v2/conversations/:id` đổi TÊN hội thoại (chỉ tên — nội dung `ai_messages` vẫn
 * append-only, không có cửa sửa). Cắt khoảng trắng hai đầu · 1..60 · cấm ký tự điều khiển (xuống dòng làm vỡ một dòng danh sách).
 * `.strict()`: body chỉ có `title` — không có đường đổi chủ/nhóm/thời điểm. Ký tự cấm: `AI_CONVERSATION_TEXT_SAFE`.
 */
export const aiConversationRenameBodySchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1)
      .max(AI_CONVERSATION_TITLE_MAX)
      .regex(AI_CONVERSATION_TEXT_SAFE),
  })
  .strict();

export const aiHistoryListResponseSchema = z
  .object({
    data: z.array(aiHistoryConversationSchema),
    pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict(),
  })
  .strict();

export const aiHistoryMessagesResponseSchema = z
  .object({
    data: z.array(aiHistoryMessageSchema),
    pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict(),
  })
  .strict();

export type AiHistoryChip = z.infer<typeof aiHistoryChipSchema>;
export type AiHistoryMessage = z.infer<typeof aiHistoryMessageSchema>;
export type AiHistoryConversation = z.infer<typeof aiHistoryConversationSchema>;
export type AiHistoryListQuery = z.infer<typeof aiHistoryListQuerySchema>;
export type AiHistoryConversationsQuery = z.infer<typeof aiHistoryConversationsQuerySchema>;
export type AiConversationRenameBody = z.infer<typeof aiConversationRenameBodySchema>;
export type AiHistoryListResponse = z.infer<typeof aiHistoryListResponseSchema>;
export type AiHistoryMessagesResponse = z.infer<typeof aiHistoryMessagesResponseSchema>;
